import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import {
  MIN_SEGMENT_LENGTH_SECONDS,
  SEGMENT_KEYBOARD_STEP,
} from '@disco/constants/search'
import { formatTime } from '@disco/utils/media-player'

type UseSegmentDragOptions = {
  segmentStart: number
  segmentEnd: number
  duration: number
  maxSegmentLength: number
  onSegmentChange?: (start: number, end: number) => void
  onSegmentCommit?: (start: number, end: number) => void
}

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(value, max))

const resolveClientX = (e: MouseEvent | TouchEvent): number | null => {
  if ('touches' in e) {
    return e.touches[0]?.clientX ?? null
  }
  return e.clientX
}

// updateAria=false on the non-drag path: React owns ARIA from JSX and
// imperative writes would clobber the undefined values on read-only instances.
const applySegmentToDOM = (
  seg: HTMLDivElement | null,
  newStart: number,
  newEnd: number,
  dur: number,
  startLabelRef: { readonly current: HTMLDivElement | null },
  endLabelRef: { readonly current: HTMLDivElement | null },
  updateAria = true,
) => {
  if (!seg || dur <= 0) return
  const el = seg
  el.style.left = `${(newStart / dur) * 100}%`
  el.style.width = `${((newEnd - newStart) / dur) * 100}%`
  if (updateAria) {
    el.setAttribute('aria-valuenow', String(Math.round(newStart)))
    el.setAttribute(
      'aria-valuetext',
      `${formatTime(newStart)} to ${formatTime(newEnd)}`,
    )
    el.setAttribute(
      'aria-label',
      `Segment: ${formatTime(newStart)} – ${formatTime(
        newEnd,
      )} (drag or use arrow keys)`,
    )
  }
  const startEl = startLabelRef.current
  if (startEl) startEl.textContent = formatTime(newStart)
  const endEl = endLabelRef.current
  if (endEl) endEl.textContent = formatTime(newEnd)
}

// Attaches mouse/touch drag listeners via AbortController; tears down on end.
const attachDragListeners = (
  abortRef: { current: AbortController | null },
  onMove: (clientX: number) => void,
  onEnd: () => void,
) => {
  const controller = new AbortController()
  const ref = abortRef // local alias to satisfy no-param-reassign
  ref.current = controller
  const { signal } = controller

  const handleMove = (e: MouseEvent | TouchEvent) => {
    if ('touches' in e) e.preventDefault()
    const clientX = resolveClientX(e)
    if (clientX !== null) onMove(clientX)
  }

  const handleEnd = () => {
    onEnd()
    ref.current?.abort()
    ref.current = null
  }

  document.addEventListener('mousemove', handleMove, { signal })
  document.addEventListener('mouseup', handleEnd, { signal })
  document.addEventListener('contextmenu', handleEnd, { signal })
  document.addEventListener('touchmove', handleMove, { passive: false, signal })
  document.addEventListener('touchend', handleEnd, { signal })
  document.addEventListener('touchcancel', handleEnd, { signal })
}

export const useSegmentDrag = ({
  segmentStart,
  segmentEnd,
  duration,
  maxSegmentLength,
  onSegmentChange,
  onSegmentCommit,
}: UseSegmentDragOptions) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const segmentRef = useRef<HTMLDivElement | null>(null)
  const startLabelRef = useRef<HTMLDivElement>(null)
  const endLabelRef = useRef<HTMLDivElement>(null)
  const pendingCommitRef = useRef<{ start: number; end: number } | null>(null)
  const pendingKeyCommitRef = useRef<{ start: number; end: number } | null>(
    null,
  )
  const onSegmentChangeRef = useRef(onSegmentChange)
  const onSegmentCommitRef = useRef(onSegmentCommit)
  const isDraggingRef = useRef(false)
  const dragOriginRef = useRef<{
    mouseX: number
    startTime: number
    segmentLength: number
    containerWidth: number
    finalStart: number
  } | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  // Assign directly in render body so event handlers never read stale values.
  onSegmentChangeRef.current = onSegmentChange
  onSegmentCommitRef.current = onSegmentCommit

  const [isDragging, setIsDragging] = useState(false)

  // Syncs position/labels after commit; skipped during drag. ARIA left to React.
  useLayoutEffect(() => {
    if (isDraggingRef.current) return
    applySegmentToDOM(
      segmentRef.current,
      segmentStart,
      segmentEnd,
      duration,
      startLabelRef,
      endLabelRef,
      false,
    )
  }, [segmentStart, segmentEnd, duration])

  // Commit any in-flight drag and remove listeners on unmount.
  useEffect(() => {
    return () => {
      abortRef.current?.abort()
      abortRef.current = null

      if (pendingKeyCommitRef.current) {
        const { start, end } = pendingKeyCommitRef.current
        onSegmentCommitRef.current?.(start, end)
        pendingKeyCommitRef.current = null
      }

      if (pendingCommitRef.current) {
        const { start, end } = pendingCommitRef.current
        onSegmentChangeRef.current?.(start, end)
        onSegmentCommitRef.current?.(start, end)
        pendingCommitRef.current = null
      } else {
        const origin = dragOriginRef.current
        if (origin) {
          if (origin.finalStart !== origin.startTime) {
            const start = origin.finalStart
            const end = origin.finalStart + origin.segmentLength
            onSegmentChangeRef.current?.(start, end)
            onSegmentCommitRef.current?.(start, end)
          }
          dragOriginRef.current = null
        }
      }
    }
  }, [])

  const initiateDrag = useCallback(
    (clientX: number) => {
      if (!onSegmentChangeRef.current || isDraggingRef.current) return
      const container = containerRef.current
      if (!container) return

      segmentRef.current?.focus()
      isDraggingRef.current = true
      setIsDragging(true)

      const segmentLength = segmentEnd - segmentStart
      dragOriginRef.current = {
        mouseX: clientX,
        startTime: segmentStart,
        segmentLength,
        containerWidth: container.getBoundingClientRect().width,
        finalStart: segmentStart,
      }

      attachDragListeners(
        abortRef,
        currentClientX => {
          const origin = dragOriginRef.current
          if (!origin) return
          // Refresh width on each move to handle responsive reflow mid-drag
          const containerWidth =
            container.getBoundingClientRect().width || origin.containerWidth
          origin.containerWidth = containerWidth
          if (!containerWidth) return
          const deltaTime =
            ((currentClientX - origin.mouseX) / containerWidth) * duration
          const newStart = clamp(
            origin.startTime + deltaTime,
            0,
            duration - origin.segmentLength,
          )
          origin.finalStart = newStart
          applySegmentToDOM(
            segmentRef.current,
            newStart,
            newStart + origin.segmentLength,
            duration,
            startLabelRef,
            endLabelRef,
          )
        },
        () => {
          const origin = dragOriginRef.current
          if (!origin) return
          const { finalStart, startTime, segmentLength: len } = origin
          isDraggingRef.current = false
          setIsDragging(false)
          dragOriginRef.current = null
          if (finalStart !== startTime) {
            onSegmentChangeRef.current?.(finalStart, finalStart + len)
            onSegmentCommitRef.current?.(finalStart, finalStart + len)
          }
        },
      )
    },
    [segmentStart, segmentEnd, duration],
  )

  const startHandleDrag = useCallback(
    (side: 'left' | 'right', e: React.MouseEvent | React.TouchEvent) => {
      if (!onSegmentChangeRef.current || isDraggingRef.current) return
      e.preventDefault()
      e.stopPropagation()
      const container = containerRef.current
      if (!container || duration <= 0) return

      pendingCommitRef.current = null
      isDraggingRef.current = true
      setIsDragging(true)

      const fixedStart = segmentStart
      const fixedEnd = segmentEnd
      let cachedRect = container.getBoundingClientRect()

      const rawClientX =
        'touches' in e && e.touches.length > 0
          ? // length > 0 guarantees the first touch exists
            e.touches[0]!.clientX
          : (e as React.MouseEvent).clientX
      const edgePosition = (side === 'left' ? fixedStart : fixedEnd) / duration
      const edgeX = cachedRect.left + edgePosition * cachedRect.width
      const grabOffsetX = isFinite(rawClientX) ? rawClientX - edgeX : 0

      attachDragListeners(
        abortRef,
        clientX => {
          const currentRect = container.getBoundingClientRect()
          if (currentRect.width > 0) cachedRect = currentRect
          if (cachedRect.width <= 0) return

          const fraction = clamp(
            (clientX - grabOffsetX - cachedRect.left) / cachedRect.width,
            0,
            1,
          )
          const time = fraction * duration

          const newStart =
            side === 'left'
              ? clamp(
                  time,
                  Math.max(0, fixedEnd - maxSegmentLength),
                  fixedEnd - MIN_SEGMENT_LENGTH_SECONDS,
                )
              : fixedStart

          const newEnd =
            side === 'right'
              ? clamp(
                  time,
                  fixedStart + MIN_SEGMENT_LENGTH_SECONDS,
                  Math.min(fixedStart + maxSegmentLength, duration),
                )
              : fixedEnd

          applySegmentToDOM(
            segmentRef.current,
            newStart,
            newEnd,
            duration,
            startLabelRef,
            endLabelRef,
          )
          pendingCommitRef.current = { start: newStart, end: newEnd }
        },
        () => {
          isDraggingRef.current = false
          setIsDragging(false)
          if (pendingCommitRef.current) {
            const { start, end } = pendingCommitRef.current
            onSegmentChangeRef.current?.(start, end)
            onSegmentCommitRef.current?.(start, end)
            pendingCommitRef.current = null
          }
        },
      )
    },
    [segmentStart, segmentEnd, duration, maxSegmentLength],
  )

  const handleBodyMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!onSegmentChangeRef.current) return
      e.preventDefault()
      initiateDrag(e.clientX)
    },
    [initiateDrag],
  )

  const handleBodyTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!onSegmentChangeRef.current || e.touches.length === 0) return
      e.preventDefault()
      // Guarded above: touches is non-empty, so the first touch exists
      initiateDrag(e.touches[0]!.clientX)
    },
    [initiateDrag],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!onSegmentChangeRef.current) return
      const len = Math.max(
        MIN_SEGMENT_LENGTH_SECONDS,
        segmentEnd - segmentStart,
      )
      let newStart: number
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        newStart = Math.max(0, segmentStart - SEGMENT_KEYBOARD_STEP)
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        newStart = Math.min(
          duration - len,
          segmentStart + SEGMENT_KEYBOARD_STEP,
        )
      } else {
        return
      }
      // Not calling onSegmentCommit here — key-repeat fires at ~30 fps; commits once on keyup instead.
      pendingKeyCommitRef.current = { start: newStart, end: newStart + len }
      onSegmentChangeRef.current(newStart, newStart + len)
    },
    [segmentStart, segmentEnd, duration],
  )

  const handleKeyUp = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
    const pending = pendingKeyCommitRef.current
    if (pending) {
      onSegmentCommitRef.current?.(pending.start, pending.end)
      pendingKeyCommitRef.current = null
    }
  }, [])

  const handleLeftHandleDrag = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => startHandleDrag('left', e),
    [startHandleDrag],
  )

  const handleRightHandleDrag = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => startHandleDrag('right', e),
    [startHandleDrag],
  )

  return {
    containerRef,
    segmentRef,
    startLabelRef,
    endLabelRef,
    handleLeftHandleDrag,
    handleRightHandleDrag,
    handleBodyMouseDown,
    handleBodyTouchStart,
    handleKeyDown,
    handleKeyUp,
    isDragging,
  }
}
